import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { DSP_PEAR_DICT_CONFIG } from 'constants/global';
import { getSiteEnv } from './global';
import { customLocalStorage } from './storage';
import { safeParse } from './string';

export const NO_EMITS = [
  'A mutation operation was attempted on a database that did not allow mutations', // 这是bigo-logger 在打开indexdb失败死循环
  'Invalid left-hand side in assignment', // 这三个一般都是控制台在输入赋值语句过程中
  'invalid assignment left-hand side',
  'Left side of assignment is not a reference',
  'Uncaught EvalError: Possible side-effect in debug-evaluate', // 一般是控制台报错
  'Unexpected end of input', // 一般是控制台在输入过程中
  'IndexedDB is not supported', // logger 的自定义错误
  'Internal error opening backing store for indexedDB.open', // 打开 indexedDB失败
  'UnknownError: Internal error.',
  'Request failed, status: 0, responseText:', // logger 的自定义错误
];

const { name, version } = require('../../package.json');

const needSentry = process.env.NEED_SENTRY || getSiteEnv() === 'prod' || getSiteEnv() === 'gray';

const releaseVersion = `${name}@${version}`;

// pear字典值配置
const pearDictsConfig =
  customLocalStorage.getItem(DSP_PEAR_DICT_CONFIG) &&
  safeParse(customLocalStorage.getItem(DSP_PEAR_DICT_CONFIG) as string);

if (needSentry) {
  // 如果为pear中配置的ru域名，则使用sentry的ru域名
  const ruDomain = pearDictsConfig?.sentryConfig?.sentryRuDomain || ['dsp.bigovideo.ru', 'pubefjjs.site'];
  const dsn = ruDomain.some((el) => new RegExp(el).test(window.location.hostname))
    ? 'https://b285056bf7cb42c1b47d765f385460ae@frontmon-sysop.bigoapp.ru/31'
    : 'https://b285056bf7cb42c1b47d765f385460ae@frontmon-sysop.bigo.sg/31';
  Sentry.init({
    dsn,
    integrations: [new Integrations.BrowserTracing({ tracingOrigins: ['localhost'] })],
    tracesSampleRate: 1.0,
    release: getSiteEnv() === 'prod' ? releaseVersion : `${name}@1.0.0`,
    environment: getSiteEnv(),
    // __logger_own_request__ 为 @bigo/logger请求失败的报错
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'NotSupportedError: The element has no supported sources.',
      /__logger_own_request__/,
      // 浏览器插件错误
      /^chrome:\/\//i,
      /^chrome-extension:/i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      ...NO_EMITS,
    ],
    beforeSend: (event, hint) =>
      [throttleError, chunkLoadError, nodeModulesError, extensionError, pearConfigError].reduce(
        (e, fn) => e && fn(e, hint),
        event
      ),
  });
}

function chunkLoadError(event, hint) {
  // 降低静态资源获取失败告警级别
  if (hint?.originalException?.toString().match(/Loading.+chunk.+failed/i)) {
    // eslint-disable-next-line no-param-reassign
    event.level = Sentry.Severity.Info;
    // eslint-disable-next-line no-param-reassign
    event.fingerprint = ['ChunkLoadError'];
  }
  return event;
}

interface ExceptionRecord {
  gap: number; // 节流次数
  allCount: number; // 总发生错误次数
  currentCount: number; // 本轮次数
}
const lastExceptionRecordMap: Record<string, ExceptionRecord> = {};

// 超过 10 次之后，每上报一次，下次上报需要触发的错误次数就要增加。也就是说会上报第 1-10,11,13,16....次错误
// 这种节流是针对每一个用户的，所以不会影响大量用户上报同一个错误
// 针对的场景是当前用户不知名原因导致死循环报错
export function canUpload(errmsg) {
  if (!errmsg) {
    // 没有错误信息，不知道什么情况，直接上报
    return true;
  }

  if (!(errmsg in lastExceptionRecordMap)) {
    // 首次触发，直接上报
    lastExceptionRecordMap[errmsg] = { currentCount: 0, allCount: 1, gap: 0 };
    return true;
  }

  const { currentCount, gap, allCount } = lastExceptionRecordMap[errmsg];

  // eslint-disable-next-line no-plusplus
  lastExceptionRecordMap[errmsg].allCount++;

  if (currentCount < gap) {
    // eslint-disable-next-line no-plusplus
    lastExceptionRecordMap[errmsg].currentCount++;
    return false;
  }

  lastExceptionRecordMap[errmsg].currentCount = 0;
  if (allCount > 9) {
    // 每上报一次，节流次数就增加 1，也就是上报次数越多，触发下一次上报需要的次数就越多
    // eslint-disable-next-line no-plusplus
    lastExceptionRecordMap[errmsg].gap++;
  }

  return true;
}

// 控制上报频率，避免短时间发生大量请求
function throttleError(event: Sentry.Event, hint?: Sentry.EventHint) {
  const error = hint?.originalException;
  const errmsg = error?.toString() || '';

  if (canUpload(errmsg)) {
    return event;
  }

  return null;
}

// node_modules内部触发的上报，降低告警等级为warning
function nodeModulesError(event: Sentry.Event) {
  const { exception } = event;

  if (exception) {
    let shouldIgnore = false;

    for (const i in event?.exception?.values?.[0]?.stacktrace?.frames) {
      if (event?.exception?.values?.[0]?.stacktrace?.frames?.[i]?.filename?.match(/node_modules/g)) {
        shouldIgnore = true;
      }
    }

    if (shouldIgnore) {
      // eslint-disable-next-line no-param-reassign
      event.level = Sentry.Severity.Info;
      // eslint-disable-next-line no-param-reassign
      event.fingerprint = ['node_modules'];
    }
  }

  return event;
}

// 插件拦截上报
function extensionError(event: Sentry.Event) {
  for (const i in event?.exception?.values?.[0]?.stacktrace?.frames) {
    if (event?.exception?.values?.[0]?.stacktrace?.frames?.[i]?.filename?.match(/chrome-extension/g)) {
      return null;
    }
  }
  return event;
}

// 过滤pear配置的错误
function pearConfigError(event: Sentry.Event, hint?: Sentry.EventHint) {
  const error = hint?.originalException;
  const errmsg = error?.toString() || '';

  if (errmsg && pearDictsConfig?.sentryConfig?.ignoreList?.some((el) => new RegExp(el, 'i').test(errmsg))) {
    return null;
  }
  return event;
}

function wrapSentryUpload<T extends Error>(TypeClass: { new (message: string): T }, level: Sentry.Severity) {
  return (
    msg: string,
    payload?: {
      status?: number;
      resDataStatus?: number;
      method?: string;
      code?: number;
      url?: string;
      data?: any;
      resData?: any;
      pearId?: number;
      error?: any;
      /** 开始时间戳 */
      startTimestamp?: number;
      /** 结束时间戳，不传则取当下的时间 */
      endTimestamp?: number;
    }
  ) => {
    if (needSentry) {
      Sentry.withScope((scope) => {
        const err = new TypeClass(msg);

        scope.setFingerprint([msg]);
        scope.setLevel(level);
        scope.setTag('errType', err.name);

        const { sentryConfig: { requestErrorList = [], ignoreList = [] } = {} } = pearDictsConfig || {
          sentryConfig: {},
        };
        if (payload) {
          const { status, resDataStatus, method, code, url, data, pearId, resData, startTimestamp, endTimestamp } =
            payload;

          // 接口报错：如果需要过滤的sentry错误包含msg，不上报
          if (resData?.retmsg && requestErrorList?.some((el) => new RegExp(el, 'i').test(resData?.retmsg))) {
            return;
          }

          status && scope.setTag('status', `${status}`);
          typeof resDataStatus !== 'undefined' && scope.setTag('resDataStatus', `${resDataStatus}`);
          method && scope.setTag('method', method);
          code && scope.setTag('code', `${code}`);
          code && scope.setFingerprint([`${code}`]);
          url && scope.setTag('api', url);
          pearId && scope.setTag('pearId', `${pearId}`);
          data && scope.setExtra('reqData', data);
          resData && scope.setExtra('response data', resData);
          scope.setExtra('time data', {
            startTimestamp: new Date(startTimestamp || Date.now()).toLocaleString(),
            endTimestamp: new Date(endTimestamp || Date.now()).toLocaleString(),
          });
        }

        // 其他错误过滤
        if (err?.message && ignoreList?.some((el) => new RegExp(el, 'i').test(err.message))) {
          return;
        }

        Sentry.captureException(err);
      });
    }
  };
}

function createErrorClass(errorName: string) {
  return class CustomError extends Error {
    constructor(message) {
      super(message);
      this.name = errorName;
    }
  };
}

// 请求错误
const RequestError = createErrorClass('Request-Error');

// pear 获取错误
const PearError = createErrorClass('Pear-Error');

// fetch 获取错误
const FetchError = createErrorClass('Fetch-Error');

const LoggerError = createErrorClass('Logger-Error');

// 网络错误
const NetworkError = createErrorClass('Network-Error');

const { Fatal: FATAL, Warning, Error: ERROR } = Sentry.Severity;
export const catchRequestError = wrapSentryUpload(RequestError, FATAL);
export const catchPearError = wrapSentryUpload(PearError, FATAL);
export const catchFetchError = wrapSentryUpload(FetchError, Warning);
export const catchLoggerError = wrapSentryUpload(LoggerError, Warning);
export const catchNetworkError = wrapSentryUpload(NetworkError, ERROR);

/**
 * 将同一类信息上报给sentry
 * @param param0 配置需要记录的信息
 */
export function emitFingerprintSentry(opts: { message?: string; fingerprint: string; error?: Error; level?: string }) {
  try {
    // 第一次请求多语言失败了，上报一下成功的多语言
    Sentry.withScope(function setScope(scope) {
      scope.setLevel((opts?.level ?? 'debug') as Sentry.Severity);
      scope.setExtra('message', opts?.message);
      scope.setFingerprint([opts?.fingerprint ?? 'common']);
      // 手动捕获异常
      Sentry.captureException(opts?.error);
    });
  } catch (e) {
    // 无需再处理
  }
}
